import { GQL } from '@aline/graphql';

import type { LaunchFieldSelection } from './launch-fields/selection';
import { formatLaunchSelectionValue, parseLaunchSelectionValue } from './launch-fields/selection';

/** Yes/No values the launch form submits for a checkbox. Slack, the hosted page, and the model XML share these. */
export const LAUNCH_CHECKBOX_OPTIONS = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
] as const;

export interface LaunchFieldSuggestionField {
  fieldId: string;
  type: string;
  picklistOptions?: readonly { value: string }[] | null;
  /** Set when the picklist takes several choices, joined by commas. */
  selection?: LaunchFieldSelection | null;
}

/**
 * A suggestion the launch form can show. A date has to be `YYYY-MM-DD`, which is
 * the value both the date control and the Slack datepicker display. A picklist
 * or checkbox value has to be one of the options the form submits. Anything else
 * is dropped, so a value the control renders blank is not still submitted.
 * Email fields are exposed as text, so a bad address is left for the launch write
 * to reject.
 */
export function keepSuggestedAlineFieldValues(
  fields: readonly LaunchFieldSuggestionField[],
  suggested: Record<string, string>
): Record<string, string> {
  const fieldsById = new Map(fields.map(field => [field.fieldId, field]));
  const kept: Record<string, string> = {};
  for (const [fieldId, value] of Object.entries(suggested)) {
    const field = fieldsById.get(fieldId);
    if (field) {
      const keptValue = keepSuggestedAlineFieldValue(field, value);
      if (keptValue) kept[fieldId] = keptValue;
    }
  }
  return kept;
}

function keepSuggestedAlineFieldValue(
  field: LaunchFieldSuggestionField,
  value: string
): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  switch (field.type) {
    case GQL.AlineFieldType.Date:
      return getIsLaunchDateValue(trimmed) ? trimmed : null;
    case GQL.AlineFieldType.Picklist:
      return field.selection
        ? getOptionValues({
            options: field.picklistOptions,
            selection: field.selection,
            value: trimmed,
          })
        : getOptionValue(field.picklistOptions, trimmed);
    case GQL.AlineFieldType.Checkbox:
      return getOptionValue(LAUNCH_CHECKBOX_OPTIONS, trimmed);
    case GQL.AlineFieldType.Number:
    case GQL.AlineFieldType.Text:
      return trimmed;
  }
  return null;
}

/** The suggested choices the field offers, up to its maximum; null when none of them fit. */
function getOptionValues({
  options,
  selection,
  value,
}: {
  options: readonly { value: string }[] | null | undefined;
  selection: LaunchFieldSelection;
  value: string;
}): string | null {
  const offered = new Set(options?.map(option => option.value));
  const kept = parseLaunchSelectionValue(value)
    .filter(choice => offered.has(choice))
    .slice(0, selection.max ?? undefined);
  return kept.length ? formatLaunchSelectionValue(kept) : null;
}

function getOptionValue(
  options: readonly { value: string }[] | null | undefined,
  value: string
): string | null {
  if (!options?.some(option => option.value === value)) return null;
  return value;
}

/** A calendar day the launch date control can show and send back unchanged. */
function getIsLaunchDateValue(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
}
